import React, { useMemo, useContext } from 'react';
import { Box, Typography, Stack, CircularProgress, Card, CardHeader, CardContent } from '@mui/material';
import { BarChart } from '@mui/x-charts/BarChart';
import { useItemTooltip } from '@mui/x-charts';
import dayjs from 'dayjs';
import { translate as t } from 'hooks/useTranslations';
import { TrainingCredentialsExpiringMetricsResponse, TrainingCredentialsExpiringMetricsResponseItem } from '../_types';
import { useTrainingContext } from '../TrainingContext';
import { AppContext } from 'views/App/context';
import useExpiringCredentials from '../_data_sources/useExpiringCredentials';
import { primary } from '_lex/_foundations/phxColors';

/**
 * Normalizes the expiring credentials data to ensure we have exactly 12 months:
 * current month + next 11 months, with zeros for missing months.
 * Past dates (except current month) are adjusted to next year.
 */
const normalizeExpiringCredentialsData = (
  data: TrainingCredentialsExpiringMetricsResponse
): TrainingCredentialsExpiringMetricsResponse => {
  const now = dayjs();
  const currentMonth = now.startOf('month');
  const nextYear = now.add(1, 'year');

  // Create a map for the 12 months we need (current month + next 11)
  const monthMap = new Map<string, TrainingCredentialsExpiringMetricsResponseItem>();

  // Process each item from the API (if data exists)
  if (data && data.length > 0) {
    data.forEach((item) => {
      let itemDate = dayjs(item.date).startOf('month');

      // If date is in the past (except current month), adjust to next year
      if (itemDate.isBefore(currentMonth, 'month')) {
        // Keep the month but move to next year
        itemDate = itemDate.year(nextYear.year());
      }

      // Ensure we're within our 12-month window
      const monthsFromNow = itemDate.diff(currentMonth, 'month');
      if (monthsFromNow >= 0 && monthsFromNow < 12) {
        const monthKey = itemDate.format('YYYY-MM');
        // If we already have data for this month, sum the values
        if (monthMap.has(monthKey)) {
          const existing = monthMap.get(monthKey)!;
          monthMap.set(monthKey, {
            ...existing,
            totalCredentials: existing.totalCredentials + item.totalCredentials,
          });
        } else {
          monthMap.set(monthKey, {
            date: itemDate.toISOString(),
            totalCredentials: item.totalCredentials,
          });
        }
      }
    });
  }

  // Generate all 12 months (current + next 11) and pad with zeros if needed
  const normalizedData: TrainingCredentialsExpiringMetricsResponse = [];
  for (let i = 0; i < 12; i++) {
    const monthDate = currentMonth.add(i, 'month');
    const monthKey = monthDate.format('YYYY-MM');
    const existingData = monthMap.get(monthKey);

    normalizedData.push(
      existingData || {
        date: monthDate.toISOString(),
        totalCredentials: 0,
      }
    );
  }

  return normalizedData;
};

const getMonthsAxis = (data: TrainingCredentialsExpiringMetricsResponse) => {
  return data?.map((item) => {
    // Parse ISO date-time format and convert to abbreviated month name
    return dayjs(item.date).format('MMM');
  });
};

const getCredentialAxis = (data: TrainingCredentialsExpiringMetricsResponse) => {
  return data?.map((item) => {
    return item.totalCredentials;
  });
};

type SeriesPoint = TrainingCredentialsExpiringMetricsResponse[number];

function ItemTooltipContent({ rawSeries }: { rawSeries: SeriesPoint[] }) {
  const item = useItemTooltip();
  if (!item) return null;

  const dataIndex = item.identifier?.dataIndex;
  if (typeof dataIndex !== 'number' || dataIndex < 0 || dataIndex >= rawSeries.length) {
    return null;
  }

  const row = rawSeries[dataIndex];

  const totalText =
    item.formattedValue ?? (typeof item.value === 'number' ? item.value.toLocaleString() : String(item.value));

  return (
    <Box
      sx={{
        p: 1,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        minWidth: 220,
      }}
    >
      <Stack spacing={0.75}>
        <Typography variant='caption'>
          <b>{dayjs(row.date).format('MMM YYYY')}</b>
        </Typography>

        <Typography
          variant='caption'
          sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
        >
          <Box
            component='span'
            sx={{ width: 10, height: 10, bgcolor: item.color, borderRadius: '50%' }}
          />
          {t('reporting.insightsCenter.expireSeriesBarChart.total')}: <b>{totalText}</b>
        </Typography>
      </Stack>
    </Box>
  );
}

export function ExpiringCredentials() {
  const { currentOrganizationId } = useContext(AppContext);
  const { queryParams } = useTrainingContext();
  const expiringCredentialsQuery = useExpiringCredentials(currentOrganizationId, queryParams);

  const { data, isLoading } = expiringCredentialsQuery;

  // Normalize the data to ensure we have exactly 12 months (current + next 11)
  const normalizedData = useMemo(() => {
    return normalizeExpiringCredentialsData(data ?? []);
  }, [data]);

  const rawSeries: SeriesPoint[] = normalizedData;
  const months = useMemo(() => getMonthsAxis(normalizedData), [normalizedData]);
  const totals = useMemo(() => getCredentialAxis(normalizedData), [normalizedData]);
  const totalValueFormatter = (v: number | null) => (v == null ? '' : `${v.toLocaleString()}`);

  // Calculate y-axis domain with minimum range of 0-5
  const yAxisDomain = useMemo(() => {
    const maxValue = Math.max(...totals, 0);
    return [0, Math.max(maxValue, 5)] as [number, number];
  }, [totals]);

  if (isLoading) {
    return (
      <Card className='lex-chart-card-white'>
        <CardHeader title={t('reporting.insightsCenter.expireSeriesBarChart.title')} />
        <CardContent>
          <Box sx={{ width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className='lex-chart-card-white'>
      <CardHeader title={t('reporting.insightsCenter.expireSeriesBarChart.title')} />
      <CardContent>
        <BarChart
          xAxis={[
            {
              id: 'months',
              data: months,
              scaleType: 'band',
              // @ts-ignore - categoryGapRatio is a valid prop but missing from TypeScript definitions
              categoryGapRatio: 0.5,
            },
          ]}
          yAxis={[
            {
              id: 'credentials',
              min: 0,
              max: yAxisDomain[1],
            },
          ]}
          series={[
            {
              data: totals,
              color: primary[800],
              valueFormatter: totalValueFormatter,
            },
          ]}
          height={400}
          tooltip={{ trigger: 'item' }}
          slots={{ itemContent: () => <ItemTooltipContent rawSeries={rawSeries} /> }}
        />
      </CardContent>
    </Card>
  );
}

export default ExpiringCredentials;
